import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import qs from "query-string";
import useFetch from "use-http";

import BootGate from "../BootGate";
import Context from "./context";

const APP_VERSION = process.env.REACT_APP_VERSION || "0.0.0";

/** BMS organization's meta.subdomain — what ?src=bms stands in for without a subdomain. */
const BMS_SUBDOMAIN = "bmsbadplaner";

/**
 * A path relative to the organization's S3 folder (like `meta.avatar`) becomes
 * a full URL; absolute URLs and root-relative paths are used as they are.
 */
const orgAssetUrl = (shortid?: string, path?: string): string | undefined => {
	if (!path) {
		return undefined;
	}
	if (/^(https?:)?\/\//.test(path) || path.startsWith("/")) {
		return path;
	}
	if (!shortid) {
		return undefined;
	}
	return `${process.env.REACT_APP_AWS_S3_URL}/organizations/${shortid}/${path}`;
};

/** Points index.html's `<link rel={rel}>` at `href` while set, then restores it. */
const useLinkHref = (rel: string, href?: string) => {
	useEffect(() => {
		const link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
		if (!href || !link) {
			return undefined;
		}
		const original = link.href;
		link.href = href;
		return () => {
			link.href = original;
		};
	}, [rel, href]);
};

const TenantProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { search } = useLocation();
	const parsed = qs.parse(search);

	// eslint-disable-next-line prefer-const
	let requestedTenant = parsed?.tenant; // Organization SHORT ID

	const params = qs.stringify({
		tenant: requestedTenant,
		fallback: process.env.REACT_APP_DEFAULT_DEV_TENANT,
	});

	const subdomain: string | undefined = React.useMemo(() => {
		const { host } = window.location;
		const chunks = host.split(".");

		const isSubdomain = chunks.length === 3 || chunks.length === 4;

		// const isHegiasDomain = chunks[1]?.includes?.("hegias");

		if (!isSubdomain /*  || !isHegiasDomain */) {
			return undefined;
		}

		return chunks[0];

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [window.location.host]);

	// Hosts without a subdomain (localhost) cannot say which organization a
	// visitor came for; ?src=bms marks the BMS flow, so it resolves to BMS
	// there. A real subdomain always wins — on deployed hosts src=bms is
	// only kept for tracking.
	const tenantHost: string | undefined =
		subdomain ?? (parsed?.src === "bms" ? BMS_SUBDOMAIN : undefined);

	/**
	 * Priorities when resolving Tenant:
	 * 1. `tenant` - Organization SHORT ID in query params
	 * 2. `x-tenant-host` - Host  where the request is coming from
	 * 3. `fallback` - Organization SHORT ID in query params
	 */
	const { loading, error, data, get, response } = useFetch(
		`/tenant?${params}`,
		{
			headers: {
				...(tenantHost && { "x-tenant-host": tenantHost }),
			},
			// The API's own server-selection timeout is 5s, so anything past 8s
			// is a network problem rather than a slow database. Waiting out
			// use-http's default here is what left users on a blank page for
			// thirty seconds during the 2026-08-25 outage.
			timeout: 8000,
		},
	);

	useEffect(() => {
		document.body.setAttribute("data-version", APP_VERSION);
	}, []);

	useEffect(() => {
		get();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Organizations can brand their subdomain with their own tab icon
	// (`meta.favicon`) and home-screen icon (`meta.faviconApple`); without
	// them, index.html's HEGIAS icons stay.
	const tenantShortid: string | undefined = data?.result?.shortid;
	useLinkHref(
		"icon",
		orgAssetUrl(tenantShortid, data?.result?.meta?.favicon),
	);
	useLinkHref(
		"apple-touch-icon",
		orgAssetUrl(tenantShortid, data?.result?.meta?.faviconApple),
	);

	return (
		<BootGate
			label="Tenant"
			loading={loading}
			error={error}
			response={response}
			onRetry={get}
			subdomain={subdomain}
		>
			<Context.Provider
				value={{
					tenant: data?.result,
					error,
					loading,
					subdomain,
					subdomainsSupported:
						process.env.REACT_APP_SUBDOMAINS_SUPPORTED?.includes(
							"true",
						) || false,
				}}
			>
				{children}
			</Context.Provider>
		</BootGate>
	);
};

export default TenantProvider;
